import type Uppy from "@uppy/core";
import { completeFileUpload, updateFileAttributes } from "@/services/api/files";
import type {
  FileUploadProgress,
  UploadProgressData,
  UploadResponse,
  UploadResult,
  UppyFileWithMeta,
} from "@/services/api/files";
import { toast } from "@/shared/lib/toast";
import {
  calculateProgressPercent,
  createAttributeValuesArray,
  extractErrorMessage,
  extractFileId,
  overrideUppyError,
  showUploadToast,
  updateProgressStatus,
} from "./uppyUtils";

interface EventHandlersContext {
  uppy: Uppy;
  fileIdMapRef: React.MutableRefObject<Map<string, string>>;
  isUploadingRef: React.MutableRefObject<boolean>;
  uploadProgressRef: React.MutableRefObject<Map<string, FileUploadProgress>>;
  setUploadProgress: React.Dispatch<
    React.SetStateAction<Map<string, FileUploadProgress>>
  >;
  setFilesUpdateTrigger: React.Dispatch<React.SetStateAction<number>>;
  refs: React.MutableRefObject<{
    categoryId: number;
    attributeValues: Record<string | number, string | number | undefined>;
    tagIds?: number[];
    onUploadComplete?: () => void;
    onUploadError?: (error: Error) => void;
  }>;
}

export function createUppyEventHandlers(context: EventHandlersContext) {
  const {
    uppy,
    fileIdMapRef,
    isUploadingRef,
    uploadProgressRef,
    setUploadProgress,
    setFilesUpdateTrigger,
    refs,
  } = context;

  const processFileCompletion = async (file: UppyFileWithMeta) => {
    try {
      const fileId = fileIdMapRef.current.get(file.id) || extractFileId(file);
      if (!fileId) throw new Error(`FileId not found for file ${file.id}`);
      fileIdMapRef.current.set(file.id, fileId);
      if (file.meta) file.meta.fileId = fileId;
      const attrs = createAttributeValuesArray(refs.current.attributeValues);
      const tagIds = refs.current.tagIds;
      await completeFileUpload(fileId, {
        fileName: file.name || file.id,
        contentType: file.type || null,
        categoryId: refs.current.categoryId,
        attributeValues: attrs,
        ...(tagIds && tagIds.length > 0 ? { tagIds } : {}),
      });
      await updateFileAttributes(fileId, { attributeValues: attrs });
      setUploadProgress((prev) =>
        updateProgressStatus(prev, file.id, {
          progress: 100,
          status: "complete",
        })
      );
    } catch (error) {
      const errMsg = extractErrorMessage(
        error,
        `Failed to process file ${file.name || file.id}`
      );
      const uppyFile = uppy.getFile(file.id);
      if (uppyFile) {
        (uppyFile as { error?: string | Error }).error = errMsg;
        uppy.emit("upload-error", uppyFile, new Error(errMsg));
      }
      overrideUppyError(uppy, file.id, errMsg);
      setUploadProgress((prev) =>
        updateProgressStatus(prev, file.id, {
          status: "error",
          error: errMsg,
        })
      );
    }
  };

  const handleUploadSuccess = async (file: unknown, response: unknown) => {
    if (!file) return;
    const f = file as UppyFileWithMeta;
    const fileId = extractFileId(f, response as UploadResponse);
    if (fileId) fileIdMapRef.current.set(f.id, fileId);
    await processFileCompletion(f);
  };

  const handleFileAdded = (file: unknown) => {
    const f = file as UppyFileWithMeta;
    if (f.meta) {
      delete f.meta.uploadUrl;
      delete f.meta.fileId;
    }
    if (f.tus) {
      delete f.tus.uploadUrl;
    }
    if (f.uploadURL) {
      delete f.uploadURL;
    }
    setUploadProgress((prev) => {
      const next = new Map(prev);
      next.set(f.id, {
        id: f.id,
        name: f.name || f.id,
        size: (f as { size?: number }).size || 0,
        progress: 0,
        status: "pending",
      });
      return next;
    });
    setFilesUpdateTrigger((p) => p + 1);
  };

  const handleFileRemoved = (file: unknown) => {
    const f = file as UppyFileWithMeta;
    setUploadProgress((prev) => {
      const next = new Map(prev);
      next.delete(f.id);
      return next;
    });
    setFilesUpdateTrigger((p) => p + 1);
  };

  const handleUpload = () => {
    if (!isUploadingRef.current) isUploadingRef.current = true;
  };

  const handleUploadProgress = (file: unknown, progress: unknown) => {
    const f = file as UppyFileWithMeta;
    const p = progress as UploadProgressData;
    if (!f || !p || !p.bytesTotal || p.bytesTotal <= 0) return;
    setUploadProgress((prev) =>
      updateProgressStatus(prev, f.id, {
        progress: calculateProgressPercent(p),
        status: "uploading",
      })
    );
  };

  const handleComplete = async (result: unknown) => {
    isUploadingRef.current = false;
    showUploadToast(result as UploadResult, uploadProgressRef);
    refs.current.onUploadComplete?.();
  };

  const handleUploadError = (file: unknown, error: unknown) => {
    const errMsg = extractErrorMessage(
      error,
      "An error occurred while uploading the file."
    );
    if (file) {
      const f = file as UppyFileWithMeta;
      toast.error(`Upload failed: ${f.name || "Unknown file"}`, errMsg);
      overrideUppyError(uppy, f.id, errMsg);
      setUploadProgress((prev) =>
        updateProgressStatus(prev, f.id, {
          status: "error",
          error: errMsg,
          progress: 0,
        })
      );
    }
    if (
      uppy.getFiles().filter((f) => f.progress?.percentage !== 100 && !f.error)
        .length === 0
    )
      isUploadingRef.current = false;
    refs.current.onUploadError?.(
      error instanceof Error ? error : new Error(errMsg)
    );
  };

  return {
    handleUpload,
    handleUploadSuccess,
    handleFileAdded,
    handleFileRemoved,
    handleUploadProgress,
    handleComplete,
    handleUploadError,
  };
}
